import React, { useMemo, useState } from "react";
import WorldUSSalesMap, { CityDataItem, StateDataItem } from "../../../components/WorldHoldoutTreatmentMap";

interface LocationCoord {
  location: string;
  locationAddress?: string | null;
  lat: number;
  long: number;
  value: number;
}

interface MarketSelectionPayload {
  type: "market_selection";
  editable: boolean;
  all_locations: string[];
  detected_markets: string[];
  selected_markets: string[];
  state_data: StateDataItem[];
  location_coords: LocationCoord[];
}

interface MarketSelectionArtifactProps {
  payload: MarketSelectionPayload;
  onConfirm: (markets: string[]) => void;
}

const MarketSelectionArtifact: React.FC<MarketSelectionArtifactProps> = ({ payload, onConfirm }) => {
  const [selected, setSelected] = useState<string[]>(payload.selected_markets || []);
  const [filter, setFilter] = useState("");

  const allLocations = payload.all_locations || [];
  const recommended = useMemo(() => new Set(payload.detected_markets || []), [payload.detected_markets]);

  const filteredLocations = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return allLocations;
    return allLocations.filter(loc => loc.toLowerCase().includes(f));
  }, [allLocations, filter]);

  const toggleMarket = (loc: string) => {
    setSelected(prev => (prev.includes(loc) ? prev.filter(m => m !== loc) : [...prev, loc]));
  };

  // Reactive map data: state fills are static (full geographic distribution),
  // city markers reflect the current selection and update instantly on toggle.
  const cityData = useMemo<CityDataItem[]>(() => {
    const stateEntries: CityDataItem[] = (payload.state_data || []).map(s => ({
      name: s.state,
      coordinates: [0, 0],
      value: s.value,
      isState: true,
    }));
    const selectedSet = new Set(selected);
    const marketEntries: CityDataItem[] = (payload.location_coords || [])
      .filter(loc => selectedSet.has(loc.location))
      .map(loc => ({
        name: loc.location,
        coordinates: [loc.long, loc.lat],
        value: loc.value,
        isState: false,
      }));
    return [...stateEntries, ...marketEntries];
  }, [payload.state_data, payload.location_coords, selected]);

  return (
    <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)", borderRadius: "var(--radius)", overflow: "hidden", animation: "fadeInUp 250ms ease" }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 15 }}>🗺️</span>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", fontFamily: "var(--font-sans)" }}>
          {payload.editable ? "Select Test Markets" : "Geographic Distribution"}
        </span>
      </div>

      {payload.editable ? (
        <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--border-subtle)" }}>
          <input
            type="text"
            placeholder="Filter markets…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ width: "100%", padding: "6px 10px", borderRadius: "var(--radius)", border: "1px solid var(--border-default)", fontSize: 12.5, fontFamily: "var(--font-sans)", marginBottom: 10, background: "var(--bg-base)", color: "var(--text-primary)" }}
          />
          <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
            {filteredLocations.map(loc => (
              <label key={loc} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", borderRadius: 6, cursor: "pointer", fontSize: 12.5, fontFamily: "var(--font-sans)", color: "var(--text-primary)" }}>
                <input
                  type="checkbox"
                  checked={selected.includes(loc)}
                  onChange={() => toggleMarket(loc)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ flex: 1 }}>{loc}</span>
                {recommended.has(loc) && <span title="DTW recommended">⭐</span>}
              </label>
            ))}
            {filteredLocations.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--text-tertiary)", padding: "8px 4px" }}>
                No markets match "{filter}".
              </div>
            )}
          </div>
          <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--text-secondary)", fontFamily: "var(--font-sans)" }}>
            {selected.length} of {allLocations.length} markets selected
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button
              onClick={() => onConfirm(selected)}
              style={{ flex: 1, padding: "8px 0", borderRadius: "var(--radius)", border: "none", background: "var(--blue)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-sans)" }}
            >
              Confirm Selection →
            </button>
          </div>
        </div>
      ) : (
        <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--border-subtle)", fontSize: 12.5, color: "var(--text-secondary)", fontFamily: "var(--font-sans)" }}>
          Markets auto-selected (Pooled SCM) — all locations are combined and analysed automatically.
        </div>
      )}

      <div className="w-full overflow-visible" style={{ padding: "12px 18px" }}>
        <WorldUSSalesMap
          stateData={payload.state_data || []}
          cityData={cityData}
          showLegend={false}
        />
      </div>
    </div>
  );
};

export default MarketSelectionArtifact;
